import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Radio, FormControlLabel, RadioGroup, Link } from '@mui/material';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

import { ScreeningPackage } from '../../types/tenantScreening';

export interface PackageFeature {
  name: string;
}

export interface ScreeningPackageOption {
  type: ScreeningPackage;
  name: string;
  price: number;
  features: string[];
  recommended?: boolean;
}

export interface ScreeningPackageSelectorProps {
  packages: ScreeningPackageOption[];
  selectedPackage?: ScreeningPackage | undefined;
  onPackageChange: (packageType: ScreeningPackage) => void;
  showSampleReportLink?: boolean | undefined;
}

export const ScreeningPackageSelector: React.FC<
  ScreeningPackageSelectorProps
> = ({
  packages,
  selectedPackage = ScreeningPackage.COMPREHENSIVE,
  onPackageChange,
  showSampleReportLink = true,
}) => {
  const handlePackageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPackageChange(event.target.value as ScreeningPackage);
  };

  return (
    <RadioGroup
      value={selectedPackage}
      onChange={handlePackageChange}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 3,
      }}
    >
      {packages.map(pkg => (
        <FormControlLabel
          key={pkg.type}
          value={pkg.type}
          control={<Radio sx={{ display: 'none' }} />}
          label={
            <Box
              sx={{
                border:
                  selectedPackage === pkg.type
                    ? '2px solid #1976d2'
                    : '1px solid #e0e0e0',
                borderRadius: 2,
                padding: 3,
                backgroundColor:
                  selectedPackage === pkg.type ? '#f3f8ff' : 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                height: '100%',
                minHeight: 320,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                '&:hover': {
                  borderColor:
                    selectedPackage === pkg.type ? '#1976d2' : '#bdbdbd',
                  backgroundColor:
                    selectedPackage === pkg.type ? '#f3f8ff' : '#f9f9f9',
                },
              }}
            >
              {pkg.recommended && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  Recommended
                </Box>
              )}

              <Typography
                variant='h5'
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: '#333',
                  mt: pkg.recommended ? 1 : 0,
                }}
              >
                {pkg.name}
              </Typography>

              <Typography
                variant='h4'
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: '#333',
                }}
              >
                ${pkg.price.toFixed(2)}
              </Typography>

              <Box sx={{ flex: 1, mb: 2 }}>
                {pkg.features.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1.5,
                    }}
                  >
                    <CheckIcon
                      sx={{
                        color: '#4caf50',
                        fontSize: 20,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#666',
                        fontSize: '0.875rem',
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {showSampleReportLink && (
                <Link
                  href='#'
                  sx={{
                    color: '#1976d2',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Sample report
                </Link>
              )}
            </Box>
          }
          sx={{ margin: 0, width: '100%' }}
        />
      ))}
    </RadioGroup>
  );
};

export default ScreeningPackageSelector;
